import React from 'react';
import { Mail, Phone, MapPin, MessageCircle, Clock, ArrowRight } from 'lucide-react';
import { Card } from './ui/card';

const PHONE_NUMBER = '60166291687';
const PHONE_DISPLAY = '+60 16-629 1687';
const EMAIL = 'teacherkalah@gmail.com';

const buildWhatsAppLink = (text) =>
  `https://wa.me/${PHONE_NUMBER}?text=${encodeURIComponent(text)}`;

const quickIntents = [
  {
    label: 'Book a Free Trial',
    icon: '🎓',
    message:
      "Hi Kalah, I'd like to book a FREE TRIAL CLASS at Read Right Academy. Please share the next available slot."
  },
  {
    label: 'Programme Enquiry',
    icon: '📚',
    message:
      "Hi Kalah, I'd like to know more about your Learn-to-Read and Learn-to-Teach programmes — fees, schedule and how to enrol."
  },
  {
    label: 'Teacher Training',
    icon: '👩‍🏫',
    message:
      "Hi Kalah, I'm a teacher / parent-educator interested in your Teacher Training programme. Could you share the upcoming intake details?"
  },
  {
    label: 'Just say hi',
    icon: '💬',
    message: "Hi Kalah! I came across the Read Right Academy website and wanted to say hello."
  }
];

const Contact = () => {
  return (
    <section
      id="contact"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-blue-50 text-[#3B4BA0] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Get In Touch
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Chat with Kalah on WhatsApp
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            The fastest way to enrol, ask a question or book a free trial.
            Tap below and you'll get a personal reply — usually within a few hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6 border-2 hover:border-[#3B4BA0] transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6 text-[#3B4BA0]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Call / WhatsApp</h3>
                  <a
                    href={`tel:+${PHONE_NUMBER}`}
                    data-testid="contact-phone-link"
                    className="text-[#3B4BA0] hover:underline"
                  >
                    {PHONE_DISPLAY}
                  </a>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-2 hover:border-[#3B4BA0] transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-[#3B4BA0]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                  <a
                    href={`mailto:${EMAIL}`}
                    data-testid="contact-email-link"
                    className="text-[#3B4BA0] hover:underline break-all"
                  >
                    {EMAIL}
                  </a>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-2 hover:border-[#3B4BA0] transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-[#3B4BA0]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Location</h3>
                  <p className="text-gray-600">Malaysia · Online classes worldwide</p>
                </div>
              </div>
            </Card>

            <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-xl p-6 border-2 border-blue-100">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-5 w-5 text-[#3B4BA0]" />
                <h4 className="font-semibold text-gray-900">Office Hours</h4>
              </div>
              <div className="space-y-1 text-sm text-gray-700">
                <p>Monday – Friday: 9:00 AM – 6:00 PM</p>
                <p>Saturday: 9:00 AM – 1:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
          </div>

          {/* WhatsApp CTA Panel */}
          <div className="lg:col-span-3">
            <Card
              data-testid="whatsapp-cta-panel"
              className="relative overflow-hidden p-8 sm:p-10 border-2 border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-teal-50 shadow-lg"
            >
              {/* Decorative blob */}
              <div
                aria-hidden="true"
                className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-emerald-200/30 blur-3xl"
              />

              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#25D366] flex items-center justify-center shadow-md">
                    <MessageCircle className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">
                      WhatsApp Kalah
                    </p>
                    <p className="text-lg font-bold text-gray-900">{PHONE_DISPLAY}</p>
                  </div>
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                  One tap. One conversation. No forms.
                </h3>
                <p className="text-gray-600 leading-relaxed mb-8">
                  Pick the topic that fits you best — your message will open in WhatsApp
                  with the right text already drafted. You can edit it before sending.
                </p>

                {/* Quick intent buttons */}
                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  {quickIntents.map((intent) => (
                    <a
                      key={intent.label}
                      href={buildWhatsAppLink(intent.message)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`whatsapp-intent-${intent.label
                        .toLowerCase()
                        .replace(/\s+/g, '-')}`}
                      className="group flex items-center justify-between gap-3 bg-white hover:bg-emerald-50 border-2 border-gray-200 hover:border-emerald-400 rounded-xl px-4 py-3 transition-all duration-300 text-left shadow-sm hover:shadow-md"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl" aria-hidden="true">
                          {intent.icon}
                        </span>
                        <span className="font-semibold text-gray-800 group-hover:text-emerald-700">
                          {intent.label}
                        </span>
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
                    </a>
                  ))}
                </div>

                {/* Primary CTA */}
                <a
                  href={buildWhatsAppLink(
                    "Hi Kalah, I'm interested in Read Right Academy. Could you share more details?"
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-primary-cta"
                  className="inline-flex items-center justify-center gap-3 w-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold text-lg px-6 py-5 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]"
                >
                  <MessageCircle className="h-6 w-6" />
                  Message us on WhatsApp
                </a>

                <p className="text-xs text-gray-500 text-center mt-4">
                  Replies typically within a few hours during office hours.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
